/**
 * Theme Events v2.0.0 — Single-source IIFE
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for theme custom event classes. Registers all classes
 * and the `NAMES` map onto `window.Theme.events.*`. Both IIFE callers (alpine
 * stores, defer scripts) and ES Module callers (web components) read from the
 * same window namespace — zero sync hazard.
 *
 * MENTAL MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * Events are observable boundary signals between subsystems:
 *
 *   alpine stores / cart engine   → dispatch ThemeEvent subclasses
 *   web components / surfaces     → addEventListener or `events.observe(...)`
 *
 * Every `new ThemeEvent(...)` auto-logs `Logger.ns('events').debug(name, detail)`
 * at construction. That means:
 *
 *   - Every dispatch is observable in Playwright via `getLogs('events')`.
 *   - Tests can `waitForEvent(namespace, name)` against the log buffer instead
 *     of attaching DOM listeners.
 *
 * LOAD ORDER
 * ─────────────────────────────────────────────────────────────────────────────
 * Must run AFTER `logger.js` and BEFORE `alpine-stores.js`. Loaded via
 * `<script defer src="events.js">` in `snippets/scripts.liquid` (IIFE, NOT
 * ES Module — alpine-stores.js needs these classes synchronously during its
 * own defer execution).
 *
 * USAGE (dispatchers)
 * ─────────────────────────────────────────────────────────────────────────────
 *   const { CartUpdateEvent } = window.Theme.events;
 *   document.dispatchEvent(new CartUpdateEvent(cart, sourceId, data, sections));
 *
 * USAGE (listeners)
 * ─────────────────────────────────────────────────────────────────────────────
 *   const { NAMES } = window.Theme.events;
 *   document.addEventListener(NAMES.cartUpdate, (e) => { ... });
 *
 *   // OR via the observed variant (logs handler invocation too):
 *   const unsub = window.Theme.events.observe(NAMES.cartUpdate, (e) => { ... });
 *   // later: unsub();
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

(function () {
  'use strict';

  window.Theme = window.Theme || {};

  // Logger is loaded before this file. Fall back to a quiet shim if not — but
  // that's a wiring bug, not a runtime expectation.
  const Logger = window.Theme.Logger || {
    ns: () => ({
      debug: () => {},
      info: () => {},
      warn: console.warn.bind(console),
      error: console.error.bind(console),
    }),
  };
  const log = Logger.ns('events');

  // ─────────────────────────────────────────────────────────────────────────────
  // Event names — the canonical taxonomy
  // ─────────────────────────────────────────────────────────────────────────────

  const NAMES = {
    // Cart domain
    cartUpdate: 'cart:update',
    cartError: 'cart:error',

    // Discount domain
    discountApplied: 'discount:applied',
    discountFailed: 'discount:failed',

    // Variant domain
    variantSelected: 'variant:selected',
    variantUpdate: 'variant:update',

    // UI surface lifecycle (Slice 3 will dispatch; classes prepared now)
    uiSurfaceRegistered: 'ui:surface-registered',
    uiSurfaceOpened: 'ui:surface-opened',
    uiSurfaceClosed: 'ui:surface-closed',
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ThemeEvent base — auto-logs in constructor
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Extends CustomEvent so `e.detail` works the natural way (no manual
  // assignment). Auto-logs at the boundary between dispatcher and listener:
  // exactly once per event, capturing name + detail.
  // ─────────────────────────────────────────────────────────────────────────────

  class ThemeEvent extends CustomEvent {
    constructor(name, detail = {}, options = {}) {
      super(name, {
        bubbles: options.bubbles !== false,
        cancelable: !!options.cancelable,
        composed: !!options.composed,
        detail,
      });
      log.debug(name, detail);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Cart events
  // ─────────────────────────────────────────────────────────────────────────────

  class CartUpdateEvent extends ThemeEvent {
    constructor(cart, sourceId = null, data = {}, sections = null) {
      super(NAMES.cartUpdate, { cart, sourceId, data, sections });
    }
  }

  class CartErrorEvent extends ThemeEvent {
    constructor(error, sourceId = null, data = {}) {
      super(NAMES.cartError, { error, sourceId, data });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Discount events
  // ─────────────────────────────────────────────────────────────────────────────

  class DiscountAppliedEvent extends ThemeEvent {
    constructor(code, cart = null, sourceId = null) {
      super(NAMES.discountApplied, { code, cart, sourceId });
    }
  }

  class DiscountFailedEvent extends ThemeEvent {
    constructor(code, reason = null, sourceId = null) {
      super(NAMES.discountFailed, { code, reason, sourceId });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Variant events
  // ─────────────────────────────────────────────────────────────────────────────

  class VariantSelectedEvent extends ThemeEvent {
    constructor(variant, sourceId = null) {
      super(NAMES.variantSelected, { variant, sourceId });
    }
  }

  class VariantUpdateEvent extends ThemeEvent {
    constructor(variant, sourceId = null) {
      super(NAMES.variantUpdate, { variant, sourceId });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // UI surface events (Slice 3 wires the dispatcher; classes prepared now)
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Surfaces (drawers, popovers, modals) self-register with $store.ui. Each
  // lifecycle transition emits an event so observers (analytics, tests,
  // sibling surfaces) can react without polling.
  // ─────────────────────────────────────────────────────────────────────────────

  class UISurfaceRegisteredEvent extends ThemeEvent {
    constructor(id, type, meta = {}) {
      super(NAMES.uiSurfaceRegistered, { id, type, meta });
    }
  }

  class UISurfaceOpenedEvent extends ThemeEvent {
    constructor(id, type, meta = {}) {
      super(NAMES.uiSurfaceOpened, { id, type, meta });
    }
  }

  class UISurfaceClosedEvent extends ThemeEvent {
    constructor(id, type, meta = {}) {
      super(NAMES.uiSurfaceClosed, { id, type, meta });
    }
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // observe(name, fn) — addEventListener with handler-side logging
  // ─────────────────────────────────────────────────────────────────────────────
  //
  // Use when you want the listen-side to show up in the log buffer too (handy
  // for debugging "did my handler fire?" without temporarily editing it).
  //
  // Hot paths can keep using native `document.addEventListener` directly —
  // every dispatch is already logged by the ThemeEvent constructor, so the
  // listen-side log is purely opt-in.
  //
  // Returns an unsubscribe function.
  // ─────────────────────────────────────────────────────────────────────────────

  function observe(name, fn, target = document) {
    const handler = (e) => {
      log.debug(`[heard] ${name}`, {
        detail: e.detail,
        handler: fn.name || 'anonymous',
      });
      return fn(e);
    };
    target.addEventListener(name, handler);
    return () => target.removeEventListener(name, handler);
  }

  // ─────────────────────────────────────────────────────────────────────────────
  // Expose
  // ─────────────────────────────────────────────────────────────────────────────

  window.Theme.events = {
    NAMES,
    ThemeEvent,
    CartUpdateEvent,
    CartErrorEvent,
    DiscountAppliedEvent,
    DiscountFailedEvent,
    VariantSelectedEvent,
    VariantUpdateEvent,
    UISurfaceRegisteredEvent,
    UISurfaceOpenedEvent,
    UISurfaceClosedEvent,
    observe,
  };

  log.info('Theme events v2.0.0 loaded');
})();
